import React, { Fragment } from 'react';
import styles from './IncidentNumberView.module.scss';
import { SFChip, SFDivider, SFScrollable, SFText } from 'sfui';
import FooterButtons from '../FooterButtons/FooterButtons';
import { Customer, CustomerContext } from 'ui-smartforce-settings';
import { formatDate, isIncidentNumberValid } from '../../../../Helpers';
import { CaseNumberField } from './CaseNumberField/CaseNumberField';
import { getCADLocation } from './Helpers/incidentNumber';
import { isEmpty } from 'lodash';
import { useIncidentNumberView } from './Hooks/useIncidentNumberView';
import { CadSubjectValueType } from '../../../../Models/CadReportValue';
import { ReportValue } from '../../../../Models';

/**
 * Builds a display name from subject first, middle, last name.
 */
export const getSubjectName = (subject: CadSubjectValueType): string => {
  return [subject.first_name, subject.middle_name, subject.last_name].join(' ');
};

/**
 * Builds a display description from subject race, ethnicity, gender, age, role.
 */
export const getSubjectDescription = (subject: CadSubjectValueType): string => {
  let descriptionArray: string[] = [];

  if (subject.race_ethnicity && subject.race_ethnicity.length > 0) {
    descriptionArray = [...subject.race_ethnicity];
  } else {
    if (subject.race && subject.race.length > 0)
      descriptionArray.push(subject.race);
    if (subject.ethnicity && subject.ethnicity.length > 0)
      descriptionArray.push(subject.ethnicity);
  }

  if (subject.gender) descriptionArray.push(subject.gender);
  if (subject.age) descriptionArray.push(subject.age.toString());

  return `${descriptionArray.join('; ')}${subject.role ? `- ${subject.role}` : ''}`;
};

export interface IncidentNumberViewProps {
  onCancel: () => void;
  onContinue: (
    incidentNumber: string,
    caseNumber: string,
    report?: ReportValue
  ) => void;
  onCleanUp: (
    incidentNumber: string,
    caseNumber: string,
    report?: ReportValue
  ) => void;
}

const IncidentNumberView = ({
  onCancel,
  onContinue,
  onCleanUp
}: IncidentNumberViewProps): React.ReactElement => {
  const customer = React.useContext(CustomerContext).customer as Customer;

  const {
    incidentNumber,
    caseNumber,
    selectedReport,
    caseNumberConfig,
    caseNumberOptions,
    onCaseNumberChange,
    continueButton
  } = useIncidentNumberView({ onContinue, onCleanUp });

  return (
    <div className={`${styles.incidentNumberView} ${styles.withFooterButtons}`}>
      <SFScrollable
        className={styles.scrollable}
        containerClassName={styles.scrollableContainer}
      >
        <div className={styles.content}>
          <h2 className={styles.title}>Add Report Numbers</h2>
          <p className={styles.subTitle}>
            You can add the CAD number if needed.
          </p>

          <div className={styles.form}>
            <h3 className={styles.numberTitle}>Incident Number</h3>
            <div className={styles.numberContainer}>
              <div>
                <SFText type="component-1-medium">{incidentNumber}</SFText>
                <SFText type="component-1" sfColor="neutral">
                  This number is automatically generated by the system.
                </SFText>
              </div>
            </div>

            <SFDivider className={styles.divider} />

            {caseNumberConfig && (
              <Fragment>
                <h3 className={styles.numberTitle}>
                  <span>Related Reports</span>
                  <SFChip label="OPTIONAL" size="small" sfColor="default" />
                </h3>
                <div className={styles.numberContainer}>
                  <CaseNumberField
                    label={caseNumberConfig.label}
                    required={caseNumberConfig.required}
                    value={
                      selectedReport
                        ? (selectedReport.general_information
                            .incident_number as string)
                        : caseNumber
                    }
                    options={caseNumberOptions}
                    onChange={onCaseNumberChange}
                    helperText={caseNumberConfig.hint}
                    error={
                      caseNumber.length > 0 &&
                      !isIncidentNumberValid(caseNumber)
                    }
                  />

                  {(!selectedReport ||
                    (selectedReport && !isEmpty(selectedReport.cad_rms))) && (
                    <SFDivider />
                  )}

                  <div className={styles.selectedReportContainer}>
                    {!selectedReport && (
                      <SFText type="component-1">
                        There is no CAD / Case Number selected.
                      </SFText>
                    )}
                    {selectedReport && !isEmpty(selectedReport.cad_rms) && (
                      <Fragment>
                        <div className={styles.reportData}>
                          <SFText type="component-messages" sfColor="neutral">
                            DATE AND TIME
                          </SFText>
                          <SFText type="component-1">
                            {formatDate(
                              selectedReport.cad_rms.cad.date_time
                                .received as string,
                              'MM/DD/YYYY - hh:mm',
                              customer.timezone
                            )}
                          </SFText>
                        </div>
                        {selectedReport.cad_rms.cad.location && (
                          <div className={styles.reportData}>
                            <SFText type="component-messages" sfColor="neutral">
                              LOCATION
                            </SFText>
                            <SFText type="component-1">
                              {getCADLocation(selectedReport.cad_rms)}
                            </SFText>
                          </div>
                        )}
                        {selectedReport.cad_rms.cad.disposition && (
                          <div className={styles.reportData}>
                            <SFText type="component-messages" sfColor="neutral">
                              DISPOSITION
                            </SFText>
                            <SFText type="component-1">
                              {selectedReport.cad_rms.cad.disposition}
                            </SFText>
                          </div>
                        )}
                        {selectedReport.cad_rms.subjects &&
                          selectedReport.cad_rms.subjects.length > 0 && (
                            <div className={styles.reportData}>
                              <SFText
                                type="component-messages"
                                sfColor="neutral"
                              >
                                SUBJECTS INFORMATION
                              </SFText>
                              {selectedReport.cad_rms.subjects.map(
                                (subject, index) => (
                                  <Fragment key={index}>
                                    <SFText type="component-1">
                                      {getSubjectName(subject)}
                                    </SFText>
                                    <SFText
                                      type="component-messages"
                                      sfColor="neutral"
                                    >
                                      {getSubjectDescription(subject)}
                                    </SFText>
                                    {index !==
                                      (selectedReport.cad_rms?.subjects
                                        ?.length ?? 0) -
                                        1 && (
                                      <SFDivider
                                        className={styles.subjectDivider}
                                      />
                                    )}
                                  </Fragment>
                                )
                              )}
                            </div>
                          )}
                      </Fragment>
                    )}
                  </div>
                </div>
              </Fragment>
            )}
          </div>
        </div>
      </SFScrollable>

      <footer>
        <FooterButtons onCancel={onCancel} buttonAction={continueButton} />
      </footer>
    </div>
  );
};

export default IncidentNumberView;
